/**
 * Full run history (ARCHITECTURE.md §8): the dedicated, keyset-paginated view of
 * every `strategy_runs` row for one strategy. The detail page shows only the
 * newest few runs and links here; this page keeps the "load older" pagination.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@richrich/i18n/react";
import { PageShell } from "../../components/PageShell";
import { strategyDetailQuery, strategyRunsInfiniteQuery } from "../../features/strategies/api";
import { RunHistoryTimeline } from "../../features/strategies/RunHistoryTimeline";

export const Route = createFileRoute("/strategies/$id/runs/")({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(strategyDetailQuery(params.id)),
      context.queryClient.ensureInfiniteQueryData(strategyRunsInfiniteQuery(params.id)),
    ]);
  },
  component: RunHistoryPage,
});

function RunHistoryPage() {
  const { t } = useI18n();
  const { id } = Route.useParams();
  const strategy = useQuery(strategyDetailQuery(id));

  if (strategy.isPending) {
    return (
      <PageShell eyebrow={t("strategies.strategyEyebrow")} title={t("common.loading")} loading />
    );
  }

  if (strategy.isError || strategy.data === undefined) {
    return (
      <PageShell eyebrow={t("strategies.strategyEyebrow")} title={t("strategies.notFound")}>
        <div className="rounded-xl border border-dashed border-hairline py-14 text-center">
          <p className="text-sm font-medium text-ink">{t("strategies.detailLoadError")}</p>
          <Link to="/strategies" className="mt-2 inline-block text-sm text-brand hover:underline">
            {t("strategies.backToStrategies")}
          </Link>
        </div>
      </PageShell>
    );
  }

  const s = strategy.data;

  return (
    <PageShell
      eyebrow={t("strategies.runsEyebrow", { slug: s.slug })}
      title={s.name}
      actions={
        <Link
          to="/strategies/$id"
          params={{ id }}
          className="rounded-md border border-hairline px-3 py-1.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-elevated"
        >
          {t("strategies.strategyAction")}
        </Link>
      }
    >
      <RunHistoryTimeline strategyId={id} />
    </PageShell>
  );
}
